import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '../../hooks/useLanguage'
import { cn } from '../../utils/cn'
import './LanguageSwitcher.css'

/**
 * LanguageSwitcher Component
 *
 * Implements Requirements:
 * - 2.1: Display language switcher in application header
 * - 2.2: Display currently active locale
 * - 2.3: Show dropdown menu with all supported languages
 * - 2.4: Change active locale immediately on selection
 * - 6.1: Display globe icon for current locale
 * - 6.2: Minimum touch target size of 44x44 pixels
 * - 6.3: Visual hover state
 * - 6.4: Display language names in native scripts
 * - 6.5: Consistent styling with design system
 *
 * Features:
 * - Dropdown UI with current language display
 * - Globe icon for visual identification
 * - Native language names (O'zbekcha, Русский, English)
 * - Smooth animations for dropdown open/close
 * - Keyboard navigation support
 * - Accessibility features (ARIA labels, roles)
 * - Responsive design
 */

export interface LanguageSwitcherProps {
  className?: string
  showLabel?: boolean
  variant?: 'default' | 'minimal'
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className,
  showLabel = false,
  variant = 'default',
}) => {
  const { currentLanguage, changeLanguage, languages, isChanging } =
    useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Get current language option
  const currentLanguageOption = languages.find(
    (lang) => lang.code === currentLanguage
  )

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  /**
   * Toggle dropdown open/close
   * Requirement 2.3: Handle dropdown open/close with click
   */
  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  /**
   * Handle language selection
   * Requirement 2.4: Change active locale immediately on selection
   */
  const handleLanguageSelect = async (languageCode: string) => {
    await changeLanguage(languageCode as any)
    setIsOpen(false)
  }

  /**
   * Handle keyboard navigation
   * Requirement 6.2: Implement keyboard navigation (Tab, Enter, Escape, Arrow keys)
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      buttonRef.current?.focus()
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (e.target === buttonRef.current) {
        e.preventDefault()
        setIsOpen(!isOpen)
      }
    }
  }

  /**
   * Handle keyboard navigation in dropdown options
   */
  const handleOptionKeyDown = (
    e: React.KeyboardEvent,
    languageCode: string
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleLanguageSelect(languageCode)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const currentIndex = languages.findIndex(
        (lang) => lang.code === languageCode
      )
      const nextIndex = (currentIndex + 1) % languages.length
      const nextButton = dropdownRef.current?.querySelector(
        `[data-language="${languages[nextIndex].code}"]`
      ) as HTMLButtonElement
      nextButton?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const currentIndex = languages.findIndex(
        (lang) => lang.code === languageCode
      )
      const prevIndex = (currentIndex - 1 + languages.length) % languages.length
      const prevButton = dropdownRef.current?.querySelector(
        `[data-language="${languages[prevIndex].code}"]`
      ) as HTMLButtonElement
      prevButton?.focus()
    }
  }

  return (
    <div
      ref={dropdownRef}
      className={cn(
        'language-switcher',
        `language-switcher--${variant}`,
        className
      )}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button - Requirement 6.2: Minimum 44x44 pixels */}
      <motion.button
        ref={buttonRef}
        type="button"
        className={cn(
          'language-switcher__trigger',
          'i18n-switcher-button',
          isOpen && 'language-switcher__trigger--open',
          isChanging && 'language-switcher__trigger--changing i18n-changing'
        )}
        onClick={handleToggle}
        disabled={isChanging}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Current language: ${currentLanguageOption?.nativeName}. Click to change language`}
        whileTap={{ scale: 0.95 }}
      >
        {/* Globe Icon - Requirement 6.1 */}
        <span className="language-switcher__icon" aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path d="M2 12H22" stroke="currentColor" strokeWidth="2" />
            <path
              d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </span>

        {/* Current Language Display - Requirement 2.2, 6.4 */}
        <span
          className={cn(
            'language-switcher__current',
            'i18n-fade-transition',
            'i18n-stable'
          )}
        >
          {currentLanguageOption?.nativeName}
        </span>

        {/* Optional Label */}
        {showLabel && (
          <span className="language-switcher__label">Language</span>
        )}

        {/* Dropdown Arrow */}
        <motion.span
          className="language-switcher__arrow"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          aria-hidden="true"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.span>
      </motion.button>

      {/* Dropdown Menu - Requirement 2.3 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={cn('language-switcher__menu', 'i18n-gpu-accelerated')}
            role="listbox"
            aria-label="Select language"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{
              duration: 0.2,
              ease: 'easeOut',
            }}
          >
            <div className="language-switcher__options">
              {languages.map((language) => (
                <motion.button
                  key={language.code}
                  type="button"
                  data-language={language.code}
                  className={cn(
                    'language-switcher__option',
                    'i18n-option',
                    language.code === currentLanguage &&
                      'language-switcher__option--selected'
                  )}
                  onClick={() => handleLanguageSelect(language.code)}
                  onKeyDown={(e) => handleOptionKeyDown(e, language.code)}
                  role="option"
                  aria-selected={language.code === currentLanguage}
                  tabIndex={0}
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Language Name in Native Script - Requirement 6.4 */}
                  <span className="language-switcher__option-name">
                    {language.nativeName}
                  </span>

                  {/* Language Code */}
                  <span className="language-switcher__option-code">
                    {language.code.toUpperCase()}
                  </span>

                  {/* Selected Indicator */}
                  {language.code === currentLanguage && (
                    <motion.span
                      className={cn(
                        'language-switcher__option-check',
                        'i18n-selected-indicator'
                      )}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                      aria-hidden="true"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.span>
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LanguageSwitcher
